import React, { PropsWithChildren, useContext, useEffect, useMemo } from "react";
import NetInfo, { useNetInfo } from "@react-native-community/netinfo";
import { AppState, Platform, StyleSheet } from "react-native";
import { useI18n } from "../hooks/i18n-hooks";
import { useTheme } from "../hooks/use-theme";
import ScreenTitle from "../components/ScreenTitle";
import Modal from "../components/Modal";
import ScreenCard from "../screens/ScreenCard";
import MarkdownView from "../components/MarkdownView";
import apolloClient from "../configs/graphql.config";
import Icon from "../components/Icon";
import Gap from "../ui_atoms/Gap";
import { AuthManagerContext } from "./AuthManager";

const NetworkManager: React.FC<PropsWithChildren> = ({ children }) => {
    const [_, setIsConnected] = React.useState<boolean>(true);
    const i18n = useI18n();
    const styles = useStyles();
    const { user, refreshUser } = useContext(AuthManagerContext);
    const netInfoState = useNetInfo();

    useEffect(() => {
        setIsConnected((oldIsConnectedState) => {
            if (netInfoState.isInternetReachable === null) {
                return oldIsConnectedState;
            }
            if (!oldIsConnectedState && netInfoState.isInternetReachable) {
                // eslint-disable-next-line no-console
                console.log("Internet connection restored. Re-run apollo queries");
                void apolloClient.refetchQueries({ include: "active" });
                // in case connection was lost during fetching user data we need to refetch the user
                if (!user) {
                    void refreshUser?.();
                }
            }
            return netInfoState.isInternetReachable;
        });
    }, [netInfoState, refreshUser, user]);

    useEffect(() => {
        const subAppState = AppState.addEventListener("change", (nextAppState) => {
            if (Platform.OS === "ios" && nextAppState == "active") {
                void NetInfo.refresh();
            }
        });
        return () => {
            if (subAppState) {
                subAppState.remove();
            }
        };
    }, []);
    return (
        <>
            {netInfoState.isInternetReachable === false && (
                <Modal animationType="none" statusBarTranslucent>
                    <ScreenTitle style={styles.title}>{i18n.t("no_network")}</ScreenTitle>
                    <ScreenCard style={styles.screenCard}>
                        <Gap gap={2} />
                        <Icon style={{ alignSelf: "center" }} name="wifi-slash" size={165} />
                        <Gap gap={2} />
                        <MarkdownView markdown={i18n.t("network_will_reconnect")} />
                    </ScreenCard>
                </Modal>
            )}
            {children}
        </>
    );
};

const useStyles = () => {
    const { gridSize, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                title: {
                    ...fonts.titleSmall,
                    alignSelf: "center",
                    marginTop: gridSize / 1.5,
                    marginBottom: gridSize,
                },
                screenCard: {
                    paddingHorizontal: gridSize / 1.5,
                },
            }),
        [fonts.titleSmall, gridSize],
    );
};

export default NetworkManager;
